import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Box,
  Card,
  CardContent,
  CircularProgress,
  Alert,
  Button,
  Typography,
  Container
} from '@mui/material';
import usePageTitle from '../../hooks/usePageTitle';
import {
  beginDriveReauth,
  clearDriveApiToken,
  markReauthExchangeSuccess,
  setDriveApiToken
} from '../../lib/auth-token';
import { driveAuthApi } from '../../lib/drive-auth-api';

/**
 * Compatibility stub for legacy /auth/sso/callback landings.
 * Prefers ?code= / ?sso_token= → POST /auth/sso/exchange (same as AuthBootstrap),
 * then navigates to /files with credentials stripped from the URL.
 */
const SSOCallback: React.FC = () => {
  usePageTitle('SSO Login');
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const handleSSOCallback = async () => {
      try {
        const code = searchParams.get('code');
        const ssoToken = searchParams.get('sso_token');
        const errorParam = searchParams.get('error');

        if (errorParam) {
          setError(decodeURIComponent(errorParam));
          setLoading(false);
          return;
        }

        if (!code && !ssoToken) {
          // No credentials on callback — send through One resume
          beginDriveReauth({ reason: 'MISSING_TOKEN', returnUrl: `${window.location.origin}/files` });
          return;
        }

        const result = await driveAuthApi.exchangeSsoCredential({
          code: code || undefined,
          token: ssoToken || undefined
        });
        const apiToken =
          result.apiToken || (result as { token?: string }).token || '';
        if (!apiToken) {
          setError('SSO authentication failed. No token received from server.');
          setLoading(false);
          return;
        }

        setDriveApiToken(apiToken);
        markReauthExchangeSuccess();
        navigate('/files', { replace: true });
      } catch (err: any) {
        clearDriveApiToken();
        setError(
          err?.body?.message ||
            err?.message ||
            'SSO authentication failed. Please try again.'
        );
        setLoading(false);
      }
    };

    void handleSSOCallback();
  }, [searchParams, navigate]);

  return (
    <Box
      sx={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        minHeight: '100vh',
        bgcolor: 'background.default'
      }}
    >
      <Container maxWidth="sm">
        <Card>
          <CardContent sx={{ textAlign: 'center', py: 4 }}>
            {loading ? (
              <>
                <CircularProgress sx={{ mb: 2 }} />
                <Typography>Completing sign-in…</Typography>
              </>
            ) : (
              <>
                <Alert severity="error" sx={{ mb: 2 }}>
                  {error}
                </Alert>
                <Button
                  variant="contained"
                  onClick={() =>
                    beginDriveReauth({
                      reason: 'MISSING_TOKEN',
                      returnUrl: `${window.location.origin}/files`
                    })
                  }
                >
                  Continue with CYNAYD One
                </Button>
              </>
            )}
          </CardContent>
        </Card>
      </Container>
    </Box>
  );
};

export default SSOCallback;
